import { addDays, type IsoDate } from './dates';
import type { Pantry } from './pantry';
import { productOf } from './price-book';
import type { Product } from './types';

/**
 * DE: Eine gekaufte oder vorhandene Menge mit Verfallsdatum (letzter nutzbarer Tag).
 * EN: A bought or existing quantity with an expiry date (last usable day).
 */
export interface Lot {
  productId: string;
  quantity: number;
  expires: IsoDate;
}

export type Stock = readonly Lot[];

/**
 * DE: Vorräte als Posten anlegen; sie halten ab `date` so lange wie das Produkt.
 * EN: Turn the pantry into lots; they last from `date` as long as the product does.
 */
export function stockFromPantry(
  pantry: Pantry,
  date: IsoDate,
  catalog: ReadonlyMap<string, Product>,
): Stock {
  return Object.entries(pantry).map(([productId, quantity]) => ({
    productId,
    quantity,
    expires: addDays(date, productOf(catalog, productId).shelfDays),
  }));
}

/**
 * DE: Menge entnehmen, zuerst aus dem Posten, der am frühesten verdirbt; Verdorbenes bleibt liegen.
 * EN: Take a quantity, first from the lot that spoils earliest; spoiled lots are left alone.
 */
export function takeFromStock(
  stock: Stock,
  productId: string,
  quantity: number,
  date: IsoDate,
): { stock: Stock; missing: number } {
  const usable = stock
    .map((lot, index) => ({ lot, index }))
    .filter(({ lot }) => lot.productId === productId && lot.expires >= date)
    .sort((a, b) => a.lot.expires.localeCompare(b.lot.expires));
  const taken = new Map<number, number>();
  let missing = quantity;
  for (const { lot, index } of usable) {
    const take = Math.min(lot.quantity, missing);
    taken.set(index, take);
    missing -= take;
  }
  const remaining = stock
    .map((lot, index) => ({ ...lot, quantity: lot.quantity - (taken.get(index) ?? 0) }))
    .filter((lot) => lot.quantity > 0);
  return { stock: remaining, missing };
}

/**
 * DE: Einkauf als neuen Posten hinzufügen.
 * EN: Add a purchase as a new lot.
 */
export function addLot(
  stock: Stock,
  product: Product,
  quantity: number,
  purchaseDate: IsoDate,
): Stock {
  return [
    ...stock,
    { productId: product.id, quantity, expires: addDays(purchaseDate, product.shelfDays) },
  ];
}

/**
 * DE: Noch nutzbare Posten an einem Tag als Vorrat zusammenfassen.
 * EN: Sum up the lots still usable on a date as a pantry.
 */
export function stockToPantry(stock: Stock, date: IsoDate): Pantry {
  const pantry: Record<string, number> = {};
  for (const lot of stock) {
    if (lot.expires >= date) pantry[lot.productId] = (pantry[lot.productId] ?? 0) + lot.quantity;
  }
  return pantry;
}
